<template>
  <PinAutoRefreshBtn
    v-if="!currentTemplate && !hide_activate_controls"
    :item="item"
    :myKey="myKey" />
  <v-dialog
    max-width="600px"
    class="template-pop-up"
    v-model="editTemplateModalToggle">
    <EditTemplateModal
      v-on:close="editTemplateModalToggle = false"
      :template="deluge.templates[item?.value]"
      :itemtype="'template'"
      v-if="editTemplateModalToggle && deluge.templates[item?.value]" />
  </v-dialog>
  <div v-if="item.value">
    <v-btn
      v-if="deluge.templates[item.value]?.name || realtime_mode"
      width="63px"
      variant="flat"
      class="mr-3 refresh-button"
      style="height: 56px !important; width: 86px !important"
      :disabled="readOnlyMode"
      @click="
        (() => {
          // Ensure scene_data exists before navigating
          if (
            (!item['scene_data'] || typeof item['scene_data'] !== 'object') &&
            item.value &&
            createOrDeleteSceneData
          ) {
            console.warn(
              '[ScenePortLayer] scene_data missing or invalid, attempting to recreate from template...'
            );
            createOrDeleteSceneData(item.value, item);
          }

          const logData = {
            templateUid: item.value,
            templateName: deluge.templates[item.value]?.display_name,
            myKey: myKey,
            sceneDataExists: !!item['scene_data'],
            sceneDataKeys: item['scene_data']
              ? Object.keys(item['scene_data'])
              : 'N/A',
            sceneDataIsEmpty: item['scene_data']
              ? Object.keys(item['scene_data']).length === 0
              : 'N/A',
            sceneData: item['scene_data'],
            itemKeys: Object.keys(item),
            itemValue: item.value,
            itemType: item.type,
          };
          console.log(
            '[ScenePortLayer] Navigate button clicked (stringified):',
            JSON.stringify(logData, null, 2)
          );

          if (item['scene_data'] && typeof item['scene_data'] === 'object') {
            openSceneOptions(
              deluge.templates[item.value],
              item['scene_data'],
              myKey,
              item['port_index']
            );
          } else {
            console.error(
              '[ScenePortLayer] Unable to navigate: scene_data is still missing/invalid after attempt to create.'
            );
          }
        })()
      ">
      <v-icon
        size="x-large"
        style="height: 33px; opacity: 0.7">
        mdi-arrow-right-bottom
      </v-icon>
    </v-btn>
    <v-btn
      v-else
      width="63px"
      variant="flat"
      class="mr-3 refresh-button"
      style="height: 56px !important; width: 86px !important"
      ::disabled="readOnlyMode"
      @click="editTemplateModalToggle = true">
      <v-icon
        size="x-large"
        style="height: 33px; opacity: 0.7">
        mdi-pencil
      </v-icon>
    </v-btn>
  </div>
  <div v-else>
    <v-btn
      :disabled="true"
      width="63px"
      variant="flat"
      class="mr-3 refresh-button"
      style="height: 56px !important; width: 86px !important">
      <v-icon
        size="x-large"
        style="height: 33px; opacity: 0.7">
        mdi-arrow-right-bottom
      </v-icon>
    </v-btn>
  </div>

  <!-- Column container for main row + expanded asset picker -->
  <div style="display: flex; flex-direction: column; width: 100%; gap: 8px">
    <!-- Main row: AssetsTemplatesDropdown + Thumbnails -->
    <div
      ref="containerRef"
      style="
        display: flex;
        width: 100%;
        align-items: start;
        gap: 8px;
        overflow: hidden;
      ">
      <div
        :style="{
          flex: '1 1 auto',
          minWidth: '240px',
          maxWidth: dropdownMaxWidth + 'px',
        }">
        <AssetsTemplatesDropdown
          mode="templates"
          :templateUid="currentTemplate?.uid"
          :item="item"
          :realtime_mode="realtime_mode"
          :readOnlyMode="readOnlyMode"
          @itemAdded="(uid, item) => createOrDeleteSceneData(uid, item)"
          @itemCleared="(item) => createOrDeleteSceneData(undefined, item)" />
      </div>
      <div
        v-if="showThumbnails && assetThumbnails.length > 0"
        ref="thumbnailScrollRef"
        class="thumbnail-scroll-container"
        :class="{ 'is-dragging': isDragging }"
        :style="{ width: `${thumbnailContainerWidth}px` }"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag">
        <div
          v-for="(thumbData, index) in assetThumbnails"
          :key="index"
          :ref="
            (el) => {
              if (el) {
                thumbnailRefs[index] = el;
                console.debug('Ref set for index', index, el);
              }
            }
          ">
          <AssetThumb
            :src="thumbData.src"
            :label="thumbData.label"
            @click="() => toggleAssetPicker(thumbData, index)"
            :title="`Click to change ${thumbData.label || 'Asset'}: ${thumbData.name || ''}`"
            style="cursor: pointer" />
        </div>
      </div>
    </div>

    <!-- Expanded asset picker row (appears below when thumbnail is clicked) -->
    <!-- Keep this separate from assetThumbnails so it doesn't disappear when filtering -->
    <div
      v-if="
        expandedAssetPicker &&
        item.scene_data &&
        item.scene_data[expandedAssetPicker.sceneDataKey]
      "
      style="display: flex; width: 100%; align-items: start">
      <SceneAsset
        :item="item.scene_data[expandedAssetPicker.sceneDataKey]"
        :myKey="expandedAssetPicker.sceneDataKey"
        :currentTemplate="currentTemplate"
        :originLevel="originLevel"
        :resetFunc="resetFunc"
        :readOnlyMode="readOnlyMode"
        :hide_activate_controls="true"
        :hide_pin_controls="true"
        @valueChanged="handleAssetChanged" />
      <v-btn
        icon
        size="small"
        variant="text"
        style="margin-left: 8px"
        @click="expandedAssetPicker = null"
        title="Close asset picker">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </div>

  <div
    style="display: flex"
    v-if="!realtime_mode || !readOnlyMode">
    <v-icon
      v-if="currentTemplate?.src_scene_data?.[myKey]"
      variant="flat"
      class="ml-3 refresh-button"
      style="height: 56px !important"
      @click="handleUserIntent(myKey, originLevel)"
      :style="unLockedMode ? 'opacity: 0.3' : 'opacity: 0.7'"
      :title="
        unLockedMode
          ? 'Value inherits from Template or Source'
          : 'Value locked. (Will not inherit from Template or Source)'
      "
      :icon="
        unLockedMode ? 'mdi-lock-open-variant-outline' : 'mdi-lock-outline'
      "></v-icon>

    <!-- <PinSendNowBtn
      v-if="!currentTemplate"
      :item="item"
      :myKey="myKey" /> -->
  </div>
</template>
<script setup>
import { computed, watch, ref, onMounted, onBeforeUnmount } from 'vue';
import { inheritanceHandler } from '../../../utils/inheritanceHandler';
import { deluge, state } from '@/stores/deluge.js';
import PinBtn from './PinBtn.vue';
import PinAutoRefreshBtn from './PinAutoRefreshBtn.vue';
import PinSendNowBtn from './PinSendNowBtn.vue';
import { isOpaque } from './utils.js';
import AssetsTemplatesDropdown from '../AssetsTemplatesDropdown.vue';
import EditTemplateModal from '../../Templates/EditTemplateModal.vue';
import AssetThumb from '../AssetThumb.vue';
import SceneAsset from './SceneAsset.vue';
import { assetSmallThumbnail } from '../../../utils/thumbnails.js';

const editTemplateModalToggle = ref(null);
const expandedAssetPicker = ref(null);
const containerRef = ref(null);
const containerWidth = ref(0);
const resizeObserver = ref(null);
const thumbnailRefs = {}; // Store refs for virtual textAreas for each thumbnail (plain object)
const thumbnailScrollRef = ref(null);

// Drag-to-scroll state
const isDragging = ref(false);
const dragStartX = ref(0);
const dragScrollLeft = ref(0);
const opaqueMode = computed(() => {
  return isOpaque(deluge, props);
});
const props = defineProps([
  'currentTemplate',
  'resetFunc',
  'originLevel',
  'item',
  'myKey',
  'hideicons',
  'safeTemplates',
  'createOrDeleteSceneData',
  'openSceneOptions',
  'realtime_mode',
  'hide_activate_controls',
  'readOnlyMode',
]);

const emit = defineEmits(['valueChanged']);

watch(
  () => props.item.value,
  () => {
    emit('valueChanged', [props.myKey, props.item.value]);
  }
);

const unLockedMode = ref(props.item.keyInherited);

function handleUserIntent(myKey, originLevel) {
  if (props.readOnlyMode) return;
  unLockedMode.value = !unLockedMode.value;
  props.item.userIntent = unLockedMode.value === true ? 'remove' : 'keep';
  if (unLockedMode.value === true) {
    props.resetFunc(myKey, originLevel);
  }
}

const isResetDisabled = computed(() => !!props.realtime_mode);

// Collect all asset entries from the nested template's scene_data
// Show ALL asset-type entries, even if they don't have thumbnails
const assetThumbnails = computed(() => {
  const thumbnails = [];

  if (!props.item.value) {
    return thumbnails;
  }

  // First check if the item itself has scene_data (instance data)
  if (props.item.scene_data && Object.keys(props.item.scene_data).length > 0) {
    for (const key of Object.keys(props.item.scene_data)) {
      const entry = props.item.scene_data[key];

      // Show ALL asset-type entries, regardless of whether they have a value or thumbnail
      if (entry?.type === 'asset') {
        let thumbnail = null;
        let assetName = null;

        if (entry.value) {
          const asset = deluge.assets[entry.value];
          if (asset) {
            thumbnail = assetSmallThumbnail(asset);
            assetName = asset.display_name || asset.name;
          }
        }

        thumbnails.push({
          src: thumbnail, // Can be null
          label: entry.label || entry.name,
          name: assetName,
          sceneDataEntry: entry,
          sceneDataKey: key,
        });
      }
    }
  }

  // Fallback: check template's src_scene_data if item has no scene_data yet
  if (thumbnails.length === 0) {
    const template = deluge.templates[props.item.value];

    if (template) {
      const sceneData = template.scene_data || template.src_scene_data;

      if (sceneData && Object.keys(sceneData).length > 0) {
        for (const key of Object.keys(sceneData)) {
          const entry = sceneData[key];

          // Show ALL asset-type entries
          if (entry?.type === 'asset') {
            let thumbnail = null;
            let assetName = null;

            if (entry.value) {
              const asset = deluge.assets[entry.value];
              if (asset) {
                thumbnail = assetSmallThumbnail(asset);
                assetName = asset.display_name || asset.name;
              }
            }

            thumbnails.push({
              src: thumbnail, // Can be null
              label: entry.label || entry.name,
              name: assetName,
              sceneDataEntry: entry,
              sceneDataKey: key,
            });
          }
        }
      }
    }
  }

  return thumbnails;
});

// Calculate dimensions for dropdown and thumbnails
const MIN_DROPDOWN_WIDTH = 240;
const THUMBNAIL_WIDTH = 90; // AssetThumb width from component
const THUMBNAIL_MARGIN_RIGHT = 8; // AssetThumb has margin-right: 8px built in
const THUMBNAIL_MARGIN_LEFT = 8; // .scene-item .asset-thumb has margin-left: 8px
const GAP = 8; // gap between parent containers

// Calculate if we have enough space to show thumbnails
const showThumbnails = computed(() => {
  // Need at least space for dropdown + one thumbnail + gap
  const minRequired = MIN_DROPDOWN_WIDTH + THUMBNAIL_WIDTH + GAP;

  return (
    containerWidth.value >= minRequired && assetThumbnails.value.length > 0
  );
});

// Calculate maximum space available for thumbnails (after ensuring dropdown has min width)
const maxThumbnailSpace = computed(() => {
  if (!showThumbnails.value) return 0;

  // Space after dropdown min width and one gap
  return Math.max(0, containerWidth.value - MIN_DROPDOWN_WIDTH - GAP);
});

// Calculate actual width for thumbnail container (all thumbnails, will scroll if needed)
const thumbnailContainerWidth = computed(() => {
  if (!showThumbnails.value || assetThumbnails.value.length === 0) return 0;

  // Each thumbnail has both margin-left and margin-right from CSS
  // Total per thumbnail = margin-left + width + margin-right
  const totalNeeded =
    assetThumbnails.value.length *
    (THUMBNAIL_MARGIN_LEFT + THUMBNAIL_WIDTH + THUMBNAIL_MARGIN_RIGHT);

  console.debug('Thumbnail calculation:', {
    count: assetThumbnails.value.length,
    widthPerThumb: THUMBNAIL_WIDTH,
    marginLeft: THUMBNAIL_MARGIN_LEFT,
    marginRight: THUMBNAIL_MARGIN_RIGHT,
    totalPerThumb:
      THUMBNAIL_MARGIN_LEFT + THUMBNAIL_WIDTH + THUMBNAIL_MARGIN_RIGHT,
    totalNeeded,
    maxSpace: maxThumbnailSpace.value,
    containerWidth: containerWidth.value,
    willReturn: Math.min(maxThumbnailSpace.value, totalNeeded),
  });

  // Take the smaller of: space available or space needed
  const result = Math.min(maxThumbnailSpace.value, totalNeeded);
  console.debug('thumbnailContainerWidth returning:', result);
  return result;
});

// Calculate max width for dropdown (shrinks as thumbnails take space)
const dropdownMaxWidth = computed(() => {
  if (!showThumbnails.value || thumbnailContainerWidth.value === 0) {
    return containerWidth.value; // Full width if no thumbnails
  }

  // Dropdown gets remaining space after thumbnails and gap
  const result = Math.max(
    MIN_DROPDOWN_WIDTH,
    containerWidth.value - thumbnailContainerWidth.value - GAP
  );
  console.debug('dropdownMaxWidth calculation:', {
    containerWidth: containerWidth.value,
    thumbnailWidth: thumbnailContainerWidth.value,
    gap: GAP,
    remaining: containerWidth.value - thumbnailContainerWidth.value - GAP,
    result,
  });
  return result;
});

onMounted(() => {
  search_templates();

  // Set up resize observer to track container width
  if (containerRef.value) {
    containerWidth.value = containerRef.value.offsetWidth;

    resizeObserver.value = new ResizeObserver((entries) => {
      for (const entry of entries) {
        containerWidth.value = entry.contentRect.width;
      }
    });

    resizeObserver.value.observe(containerRef.value);
  }
});

onBeforeUnmount(() => {
  // Clean up resize observer
  if (resizeObserver.value && containerRef.value) {
    resizeObserver.value.unobserve(containerRef.value);
    resizeObserver.value.disconnect();
  }
});

function search_templates() {
  const templates_preset = Object.values(deluge.templates).filter(
    (template) => template?.extra_info?.preset
  );
}

// Function to toggle the asset picker for a specific thumbnail
function toggleAssetPicker(thumbData, index) {
  if (props.readOnlyMode || props.realtime_mode) {
    return;
  }

  // Toggle: if clicking the same one, close it; otherwise open the new one
  if (expandedAssetPicker.value?.sceneDataKey === thumbData.sceneDataKey) {
    expandedAssetPicker.value = null;
  } else {
    expandedAssetPicker.value = thumbData;
  }
}

// Handle when asset value changes in the SceneAsset component
function handleAssetChanged(event) {
  const [key, value] = event;
  console.log('[ScenePortLayer] Asset changed:', { key, value });

  // The SceneAsset component already updates the item.value directly
  // We don't need to emit valueChanged because:
  // 1. The change is to nested scene_data, not to this component's item
  // 2. The parent SceneData.vue would try to look up currentObj.value[key] which doesn't exist
  // 3. The reactivity is already handled by Vue since we're modifying the item reference

  // Don't auto-close the picker - let the user close it manually with the X button
  // This allows them to search, select, and change their mind without the picker disappearing
}

// Drag-to-scroll functions for thumbnail container
function startDrag(e) {
  if (!thumbnailScrollRef.value) return;
  isDragging.value = true;
  dragStartX.value = e.pageX - thumbnailScrollRef.value.offsetLeft;
  dragScrollLeft.value = thumbnailScrollRef.value.scrollLeft;
}

function onDrag(e) {
  if (!isDragging.value || !thumbnailScrollRef.value) return;
  e.preventDefault();
  const x = e.pageX - thumbnailScrollRef.value.offsetLeft;
  const walk = (x - dragStartX.value) * 1.5; // Scroll speed multiplier
  thumbnailScrollRef.value.scrollLeft = dragScrollLeft.value - walk;
}

function stopDrag() {
  isDragging.value = false;
}

watch(
  () => props.item.value,
  () => {
    inheritanceHandler(props.item);
  },
  // It's required to run this watcher immediately
  // else the value will change before the handler tracks the initial value
  { immediate: true }
);
</script>

<style scoped>
.thumbnail-scroll-container {
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;
}

.thumbnail-scroll-container.is-dragging {
  cursor: grabbing;
}

/* Prevent AssetThumb from shrinking inside the scroll container */
.thumbnail-scroll-container :deep(.asset-thumb) {
  flex-shrink: 0;
  pointer-events: none;
}

.thumbnail-scroll-container:not(.is-dragging) :deep(.asset-thumb) {
  pointer-events: auto;
}

/* Hide scrollbar for webkit browsers */
.thumbnail-scroll-container::-webkit-scrollbar {
  display: none;
}
</style>
